import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Save, Plus, Trash2, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { serviceIconNames, getServiceIcon } from '../lib/serviceIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyService = { title: '', description: '', icon: 'Compass', features: [] };

const AdminServices = () => {
  const { adminAuth } = useOutletContext();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/services`);
      if (res.ok) setContent(await res.json());
    } catch (e) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, val) => setContent((prev) => ({ ...prev, [key]: val }));

  const updateService = (idx, key, val) => {
    const next = [...content.services];
    next[idx] = { ...next[idx], [key]: val };
    setField('services', next);
  };

  const addService = () => setField('services', [...content.services, { ...emptyService }]);
  const removeService = (idx) => setField('services', content.services.filter((_, i) => i !== idx));

  const updateFeature = (sIdx, fIdx, val) => {
    const next = [...content.services];
    const features = [...next[sIdx].features];
    features[fIdx] = val;
    next[sIdx] = { ...next[sIdx], features };
    setField('services', next);
  };
  const addFeature = (sIdx) => {
    const next = [...content.services];
    next[sIdx] = { ...next[sIdx], features: [...next[sIdx].features, ''] };
    setField('services', next);
  };
  const removeFeature = (sIdx, fIdx) => {
    const next = [...content.services];
    next[sIdx] = { ...next[sIdx], features: next[sIdx].features.filter((_, i) => i !== fIdx) };
    setField('services', next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...content,
        services: content.services.map((s) => ({ ...s, features: s.features.filter((f) => f.trim()) })),
      };
      const res = await fetch(`${API_URL}/api/admin/services`, {
        method: 'PUT',
        headers: { Authorization: `Basic ${adminAuth}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Services page updated');
      } else {
        toast.error('Failed to save');
      }
    } catch (e) {
      toast.error('Connection error');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Services Page</h1>
          <p className="text-gray-600">Edit the content shown on the public Services page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-services-btn" className="bg-amber-600 hover:bg-amber-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Section Header</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <Input data-testid="services-heading-input" value={content.heading} onChange={(e) => setField('heading', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subheading</label>
            <Textarea data-testid="services-subheading-input" rows={2} value={content.subheading} onChange={(e) => setField('subheading', e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Service Cards</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addService} data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Service
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {content.services.map((service, sIdx) => {
            const Icon = getServiceIcon(service.icon);
            return (
              <div key={sIdx} data-testid={`service-card-${sIdx}`} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-gray-500">
                    <GripVertical className="w-4 h-4" />
                    <span className="text-sm font-medium">Service {sIdx + 1}</span>
                  </div>
                  <button type="button" onClick={() => removeService(sIdx)} className="p-2 text-red-500 hover:bg-red-50 rounded" data-testid={`remove-service-${sIdx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title</label>
                    <Input data-testid={`service-title-${sIdx}`} value={service.title} onChange={(e) => updateService(sIdx, 'title', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Icon</label>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                        <Icon className="w-5 h-5 text-amber-600" />
                      </div>
                      <select
                        data-testid={`service-icon-${sIdx}`}
                        value={service.icon}
                        onChange={(e) => updateService(sIdx, 'icon', e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                      >
                        {serviceIconNames.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <Textarea data-testid={`service-desc-${sIdx}`} rows={2} value={service.description} onChange={(e) => updateService(sIdx, 'description', e.target.value)} />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium">Features</label>
                    <Button type="button" variant="outline" size="sm" onClick={() => addFeature(sIdx)} data-testid={`add-feature-${sIdx}`}>
                      <Plus className="w-3 h-3 mr-1" /> Add Feature
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {service.features.map((feature, fIdx) => (
                      <div key={fIdx} className="flex items-center gap-2">
                        <Input
                          data-testid={`service-${sIdx}-feature-${fIdx}`}
                          value={feature}
                          onChange={(e) => updateFeature(sIdx, fIdx, e.target.value)}
                          placeholder="e.g. Private transfers"
                        />
                        <button type="button" onClick={() => removeFeature(sIdx, fIdx)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
          {content.services.length === 0 && <p className="text-sm text-gray-500">No services yet. Click "Add Service".</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Highlight Banner ("Your Trip, Your Way")</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Heading</label>
            <Input data-testid="usp-heading-input" value={content.usp_heading} onChange={(e) => setField('usp_heading', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Text</label>
            <Textarea data-testid="usp-text-input" rows={3} value={content.usp_text} onChange={(e) => setField('usp_text', e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Badge Text</label>
            <Input data-testid="usp-badge-input" value={content.usp_badge} onChange={(e) => setField('usp_badge', e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default AdminServices;
